import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, Package, MapPin, Mail, Truck, MessageCircle, Home, Loader2, XCircle, Receipt,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { whatsappLinks } from "@/lib/whatsapp";
import { api as apiClient } from "@/lib/api";
import { useCart } from "@/lib/cart";

const fmt = (n) => `€${Number(n).toFixed(2)}`;

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();

  // status: pending | paid | expired | error
  const [status, setStatus] = useState(sessionId ? "pending" : "paid");
  const [order, setOrder] = useState(() => {
    try {
      const raw = sessionStorage.getItem("hdh_last_order");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const pollRef = useRef({ attempts: 0 });

  // Polling for Stripe-backed payments
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const maxAttempts = 6;
    const intervalMs = 2000;

    const poll = async () => {
      if (cancelled) return;
      pollRef.current.attempts += 1;
      try {
        const { data } = await apiClient.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          // Build summary from pending + response
          const pending = (() => {
            try { return JSON.parse(sessionStorage.getItem("hdh_pending_order") || "null"); }
            catch { return null; }
          })();
          const built = {
            orderId: data.order_id || `STR-${sessionId.slice(-7)}`,
            contact: pending?.contact || {},
            shipping: pending?.shipping || { id: "standard", name: "Versand", desc: "" },
            payment: pending?.payment || "card",
            items: pending?.items || [],
            subtotal: pending?.subtotal || (data.amount_total / 100),
            shippingFee: pending?.shippingFee || 0,
            tax: pending?.tax || 0,
            grandTotal: (data.amount_total || 0) / 100,
            created_at: new Date().toISOString(),
            stripe_session_id: sessionId,
          };
          sessionStorage.setItem("hdh_last_order", JSON.stringify(built));
          sessionStorage.removeItem("hdh_pending_order");
          setOrder(built);
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (pollRef.current.attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else if (data.payment_status === "unpaid" && data.status === "open") {
          // Session still open & unpaid after retries → customer cancelled
          setStatus("expired");
        } else {
          setStatus("error");
        }
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  // If we got here without sessionId and have no order, go home
  useEffect(() => {
    if (!sessionId && !order) navigate("/", { replace: true });
  }, [sessionId, order, navigate]);

  if (status === "pending") {
    return <StatusShell icon={<Loader2 className="w-10 h-10 text-[#FF2D40] animate-spin" />} kicker="Wird verarbeitet" title="Zahlung wird bestätigt…" subtitle="Wir prüfen Ihre Zahlung bei Stripe. Bitte schließen Sie diese Seite nicht." testid="status-pending" />;
  }
  if (status === "expired") {
    return <StatusShell icon={<XCircle className="w-10 h-10 text-zinc-400" />} kicker="Abgelaufen" title="Zahlung abgebrochen oder abgelaufen" subtitle="Versuchen Sie es erneut – Ihr Warenkorb ist noch da." testid="status-expired" cta={<Link to="/checkout"><Button className="bg-[#DC1928] hover:bg-[#B01829] rounded-full h-12 px-7" data-testid="retry-checkout-btn">Erneut versuchen</Button></Link>} />;
  }
  if (status === "error") {
    return <StatusShell icon={<XCircle className="w-10 h-10 text-amber-400" />} kicker="Fehler" title="Zahlungsstatus konnte nicht bestätigt werden" subtitle="Bitte kontaktieren Sie uns – wir prüfen den Status manuell." testid="status-error" cta={<Link to="/"><Button variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white rounded-full h-12 px-7">Zur Startseite</Button></Link>} />;
  }

  if (!order) {
    // Never render an empty (black) page — always show something actionable.
    return (
      <StatusShell
        icon={<CheckCircle2 className="w-10 h-10 text-emerald-400" />}
        kicker="Danke"
        title="Bestellung erhalten"
        subtitle="Die Bestelldetails konnten in diesem Browser nicht mehr angezeigt werden. Sie erhalten die Bestätigung und Rechnung per E-Mail."
        testid="status-no-details"
        cta={<Link to="/"><Button className="bg-[#DC1928] hover:bg-[#B01829] rounded-full h-12 px-7" data-testid="no-details-home-btn">Zur Startseite</Button></Link>}
      />
    );
  }

  const wa = whatsappLinks.order({
    orderId: order.orderId,
    items: order.items,
    total: order.grandTotal,
  });

  const eta = new Date();
  eta.setDate(eta.getDate() + (order.shipping?.id === "express" ? 1 : order.shipping?.id === "pickup" ? 0 : 3));
  const etaStr = eta.toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "long" });

  return (
    <div className="pt-28 pb-20 bg-[#0A0A0B] min-h-screen text-white notranslate" translate="no" data-testid="checkout-success">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", duration: 0.7 }}
          className="mx-auto w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </motion.div>

        <div className="text-center mt-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400 font-bold">Erfolgreich</div>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-3 leading-[1.02]">Danke für<br/>Ihre Bestellung.</h1>
          <p className="text-zinc-400 mt-4">Wir haben Ihre Bestellung erhalten und sind schon dabei.</p>
          {order.stripe_session_id && (
            <p className="text-xs text-zinc-600 mt-2 inline-flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> Stripe Rechnung wird per E-Mail zugestellt
            </p>
          )}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
            <Package className="w-4 h-4 text-[#FF2D40]" />
            <span className="text-sm font-medium" data-testid="order-id">Bestellnummer · <span className="font-mono">#{order.orderId}</span></span>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <Block icon={Truck} title="Voraussichtliche Lieferung">
            <div className="font-display text-lg font-semibold text-white capitalize">{etaStr}</div>
            <div className="text-xs text-zinc-500 mt-1">{order.shipping?.name} · {order.shipping?.desc}</div>
          </Block>
          <Block icon={MapPin} title="Lieferadresse">
            <div className="text-sm text-zinc-300">{order.contact?.first_name} {order.contact?.last_name}</div>
            <div className="text-sm text-zinc-400">{order.contact?.street}</div>
            <div className="text-sm text-zinc-400">{order.contact?.zip} {order.contact?.city}, {order.contact?.country}</div>
          </Block>
        </div>

        {order.items?.length > 0 && (
          <div className="mt-4 p-6 rounded-3xl bg-[#141417] border border-white/5" data-testid="order-items">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500">Ihre Artikel</div>
            <div className="mt-4 space-y-3">
              {order.items.map((it) => (
                <div key={it.sku} className="flex gap-3 items-center" data-testid={`success-item-${it.sku}`}>
                  <div className="w-14 h-14 rounded-xl bg-[#0A0A0B] overflow-hidden border border-white/5 shrink-0">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white line-clamp-1">{it.name}</div>
                    <div className="text-xs text-zinc-500">Menge: {it.qty}</div>
                  </div>
                  <div className="font-display font-semibold numeric">{fmt(it.price * it.qty)}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-white/5 space-y-1.5 text-sm">
              <Row label="Zwischensumme" value={fmt(order.subtotal)} />
              <Row label="Versand" value={order.shippingFee === 0 ? "Gratis" : fmt(order.shippingFee)} />
              <Row label="USt. (20%)" value={fmt(order.tax)} muted />
              <div className="flex items-center justify-between pt-2">
                <span className="font-display">Gesamt</span>
                <span className="font-display text-2xl font-bold numeric">{fmt(order.grandTotal)}</span>
              </div>
            </div>
          </div>
        )}

        {order.contact?.email && (
          <div className="mt-6 p-5 rounded-3xl bg-[#0F0F11] border border-white/5 flex items-center gap-3">
            <Mail className="w-5 h-5 text-zinc-400" />
            <div className="flex-1 text-sm">
              <span className="text-zinc-400">Bestätigung wurde gesendet an</span>{" "}
              <span className="text-white font-medium">{order.contact.email}</span>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/" className="flex-1">
            <Button variant="outline" className="w-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full h-12" data-testid="success-home-btn">
              <Home className="w-4 h-4 mr-2" /> Zur Startseite
            </Button>
          </Link>
          <a href={wa.href} onClick={wa.onClick} target="_blank" rel="noreferrer" className="flex-1">
            <Button className="w-full bg-[#25D366] hover:bg-[#1ebd5b] text-white rounded-full h-12" data-testid="success-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Update anfordern
            </Button>
          </a>
        </div>

        <p className="text-center text-[11px] text-zinc-600 mt-8">
          Bei Fragen erreichen Sie uns Mo–Fr 09:00–19:00 · <a href="tel:+436764440304" className="underline">+43 676 4440304</a>
        </p>
      </div>
    </div>
  );
}

function StatusShell({ icon, kicker, title, subtitle, cta, testid }) {
  return (
    <div className="pt-28 pb-20 bg-[#0A0A0B] min-h-screen text-white" data-testid={testid}>
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="mx-auto w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">{icon}</div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold mt-8">{kicker}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">{title}</h1>
        <p className="text-zinc-400 mt-4">{subtitle}</p>
        {cta && <div className="mt-8">{cta}</div>}
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <div className="p-5 rounded-3xl bg-[#141417] border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#FF2D40]" />
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-display font-medium numeric ${muted ? "text-zinc-400" : "text-white"}`}>{value}</span>
    </div>
  );
}
